import { Logic } from '~common/ui-logic/logic'
import { executeTask, TaskState } from '~common/ui-logic/tasks'
import type { TextFieldProps } from './index'
import type React from 'react'
import { sleep } from '~common/utils/sleep'
import { debounce } from '~common/utils/debounce'

export interface TextFieldDependencies extends TextFieldProps {}

export type TextFieldState = {
    loadState: TaskState
    editValue: string
    isActive: boolean
    hasHighlighted: boolean
    anchorWidth: number | undefined
    isPopupOpen: boolean
    userNavigated: boolean
    // Dropdown options mappings
    keyToLabel: Record<string, string>
    labelToKey: Record<string, string>
    dropdownLabels: string[]
    disableOnBlur: boolean
    isHovered: boolean
}

export class TextFieldLogic extends Logic<
    TextFieldDependencies,
    TextFieldState
> {
    private currentDebounceDelay: number = 300
    private debouncedOnChange = debounce((value: string) => {
        this.deps.onChange?.(value)
    }, 300)

    private callOnChange = (value: string) => {
        const { debounceMs } = this.deps
        const delay = debounceMs === undefined ? 300 : debounceMs

        if (delay === 0) {
            this.deps.onChange?.(value)
            return
        }

        if (delay !== this.currentDebounceDelay) {
            this.currentDebounceDelay = delay
            this.debouncedOnChange = debounce((val: string) => {
                this.deps.onChange?.(val)
            }, delay)
        }

        this.debouncedOnChange(value)
    }

    getInitialState = (): TextFieldState => {
        const { initValue, initActive, dropdown, options } = this.deps

        const keyToLabel: Record<string, string> = this.computeKeyToLabel(
            options ?? [],
        )
        const labelToKey: Record<string, string> = this.computeLabelToKey(
            options ?? [],
        )
        const dropdownLabels: string[] = this.computeFilteredDropdownLabels(
            initValue || '',
        )

        let initialValue = initValue || ''

        if (dropdown && options) {
            initialValue = keyToLabel[initialValue] || initialValue
        }

        return {
            loadState: 'pristine',
            editValue: initialValue,
            isActive: initActive ?? true,
            hasHighlighted: false,
            anchorWidth: undefined,
            isPopupOpen: false,
            userNavigated: false,
            keyToLabel,
            labelToKey,
            dropdownLabels,
            disableOnBlur: false,
            isHovered: false,
        }
    }

    async initialize() {
        const { initValue, controlledValue, dropdown, options } = this.deps
        const valueToUse =
            controlledValue !== undefined ? controlledValue : initValue

        if (dropdown && options) {
            const keyToLabel = this.computeKeyToLabel(options)
            const labelToKey = this.computeLabelToKey(options)
            const dropdownLabels = this.computeFilteredDropdownLabels(
                valueToUse || '',
            )

            const displayValue =
                keyToLabel[valueToUse || ''] || valueToUse || ''
            this.setState({
                editValue: displayValue,
                keyToLabel,
                labelToKey,
                dropdownLabels,
            })
        } else {
            this.setState({
                editValue: valueToUse || '',
            })
        }
    }

    shouldReload(
        oldDeps: TextFieldDependencies,
        newDeps: TextFieldDependencies,
    ): boolean {
        return oldDeps.controlledValue !== newDeps.controlledValue
    }

    setIsHovered = (hovered: boolean) => {
        this.setState({ isHovered: hovered })
    }

    private computeKeyToLabel = (
        options: string[] | Record<string, string>,
    ): Record<string, string> => {
        const isDictionaryOptions = this.isDictionaryOptions(options)
        if (!isDictionaryOptions) return {}
        const dict = options as Record<string, string>
        return dict
    }

    private computeLabelToKey = (
        options: string[] | Record<string, string>,
    ): Record<string, string> => {
        const isDictionaryOptions = this.isDictionaryOptions(options)
        if (!isDictionaryOptions) return {}
        const dict = options as Record<string, string>
        const entries = Object.entries(dict)
        const map: Record<string, string> = {}
        for (const [key, label] of entries) {
            map[label] = key
        }
        return map
    }

    private computeDropdownLabels = (
        options: string[] | Record<string, string>,
    ): string[] => {
        const isDictionaryOptions = this.isDictionaryOptions(options)
        if (!isDictionaryOptions) return (options as string[]) ?? []
        const dict = options as Record<string, string>
        // Sort by label for nicer UX
        return Object.values(dict).sort((a, b) =>
            a.localeCompare(b, undefined, { sensitivity: 'base' }),
        )
    }

    private computeFilteredDropdownLabels = (query: string): string[] => {
        const { options } = this.deps
        if (!options) return []

        const allLabels = this.computeDropdownLabels(options)
        if (!query.trim()) return allLabels

        return allLabels.filter((label) =>
            label.toLocaleLowerCase().includes(query.toLocaleLowerCase()),
        )
    }

    private isDictionaryOptions = (
        options: string[] | Record<string, string>,
    ): boolean => {
        return Boolean(
            this.deps.dropdown &&
                typeof options === 'object' &&
                !Array.isArray(options),
        )
    }

    updateEditValue = (value: string) => {
        this.setState({ editValue: value })
    }

    setIsActive = (active: boolean) => {
        this.setState({ isActive: active })
    }

    setHasHighlighted = (highlighted: boolean) => {
        this.setState({ hasHighlighted: highlighted })
    }

    setAnchorWidth = (width: number | undefined) => {
        this.setState({ anchorWidth: width })
    }

    setIsPopupOpen = (open: boolean) => {
        this.setState({ isPopupOpen: open })
    }

    setUserNavigated = (navigated: boolean) => {
        this.setState({ userNavigated: navigated })
    }

    handleClick = () => {
        const { editable, onClick } = this.deps
        if (editable) {
            this.setIsActive(true)
        }
        onClick?.()
    }

    handleSave = () => {
        this.deps.onConfirm?.(this.state.editValue)
    }

    handleCancel = () => {
        const { initValue } = this.deps
        this.setState({ editValue: initValue || '' })
    }

    handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            // Cmd+Enter (Mac) or Ctrl+Enter (Windows/Linux) triggers onConfirm
            if (e.metaKey || e.ctrlKey) {
                this.deps.onConfirm?.(this.state.editValue)
                return
            }
            // Regular Enter only triggers onConfirm for non-multiline fields
            if (!this.deps.multiline) {
                this.deps.onConfirm?.(this.state.editValue)
            }
        } else if (e.key === 'Escape') {
            this.handleCancel()
        }
    }

    handleBlur = () => {
        const { editable, initValue, initActive, onConfirm, confirmOnBlur } =
            this.deps
        const { editValue } = this.state

        if (!editable) {
            return
        }

        if (editValue === '' && initActive != null && !initActive) {
            this.setState({ editValue: initValue || '' })
        }

        if (
            editValue.length === 0 &&
            editValue !== initValue &&
            initActive != null &&
            !initActive
        ) {
            this.setIsActive(false)
        }

        if (confirmOnBlur && onConfirm) {
            onConfirm(editValue)
        }
    }

    handleClear = async (shiftKey?: boolean) => {
        this.setState({ editValue: '' })
        await sleep(100)
        this.deps.onClear?.(shiftKey)
        this.handleBlur()
    }

    handleInputChange = (value: string) => {
        const { editable, onChange, inputValidation } = this.deps

        if (!editable) {
            return
        }

        // Apply validation if specified
        if (inputValidation?.includes('numbersOnly')) {
            // Allow numbers, decimal point, minus sign, and spaces (for ranges like "10 - 20")
            const numericRegex = /^[0-9.\-\s]*$/
            if (!numericRegex.test(value)) {
                return // Don't update if invalid
            }
        }

        this.setState({ editValue: value })
        // Update dropdown labels when in dropdown mode
        if (this.deps.dropdown && this.deps.options) {
            const filteredLabels = this.computeFilteredDropdownLabels(value)
            this.setState({ dropdownLabels: filteredLabels })
        }
        this.callOnChange(value)
    }
    handleComboboxValueChange = (value: string) => {
        const { options, dropdown } = this.deps
        const { labelToKey } = this.state
        const isDictionaryOptions =
            dropdown && typeof options === 'object' && !Array.isArray(options)

        this.setState({ editValue: value })

        // Treat empty value as clear via native combobox clear
        if (value == null || value === '') {
            this.callOnChange('')
            this.deps.onClear?.()
            this.setState({ editValue: '' })
            return
        }

        if (isDictionaryOptions && labelToKey[value]) {
            this.callOnChange(labelToKey[value])
        } else {
            this.callOnChange(value)
        }
    }

    handleComboboxOpenChange = (open: boolean) => {
        this.setIsPopupOpen(open)
        if (open) {
            this.setUserNavigated(false)
            // When opening the dropdown with an existing selection, show all options
            if (this.deps.options) {
                const allLabels = this.computeFilteredDropdownLabels('')
                this.setState({ dropdownLabels: allLabels })
            }
        }
    }

    handleComboboxKeyDown = (e: React.KeyboardEvent, query: string) => {
        if (e.key !== 'Enter') return

        // Cmd+Enter (Mac) or Ctrl+Enter (Windows/Linux) triggers onConfirm
        if (e.metaKey || e.ctrlKey) {
            e.preventDefault()
            this.handleSave()
            return
        }

        const { dropdownLabels } = this.state

        // If Base UI already has a highlighted item, let it handle Enter
        const hasHighlighted = !!document.querySelector('[data-highlighted]')
        if (hasHighlighted) return

        const firstMatch = dropdownLabels.find((opt) =>
            opt.toLocaleLowerCase().includes(query.toLocaleLowerCase()),
        )
        if (!firstMatch) return

        e.preventDefault()
        this.setState({ editValue: firstMatch })

        const { options, dropdown } = this.deps
        const { labelToKey } = this.state
        const isDictionaryOptions =
            dropdown && typeof options === 'object' && !Array.isArray(options)

        const firstMatchKey = isDictionaryOptions
            ? labelToKey[firstMatch]
            : firstMatch
        this.callOnChange(firstMatchKey)

        this.handleSave()
    }

    handleComboboxKeyUp = (e: React.KeyboardEvent) => {
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            this.setUserNavigated(true)
        }
    }
}
